import { ComponentProps, useRef } from 'react';

import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.js';
import { Alert } from '@sola/ark-core/molecules/alert/alert.js';
import i18n from '@sola/locales/index.js';

import { LocalAlertConfig } from '../../types/localeAlerts';
import { useLocalAlerts } from './local-alerts-provider';

/**
 * Single local alert.
 */
export const LocalAlert = ({
  alert,
  onClose,
  className,
}: {
  alert: LocalAlertConfig;
  onClose: (id: string) => void;
  className?: string;
}) => {
  const ref = useRef<HTMLDivElement | null>(null);

  const { translationKey, translationData, id, ...alertProps } = alert;
  const hasMsg = i18n.exists(`my:${translationKey}.message`);
  const hasTitle = i18n.exists(`my:${translationKey}.title`);
  const translation = hasMsg ? (
    <IntlBlock
      i18nKey={`my:${translationKey}.message`}
      values={translationData}
    />
  ) : null;

  const title = hasTitle ? (
    <IntlBlock
      i18nKey={`my:${translationKey}.title`}
      values={translationData}
      tOptions={{
        interpolation: {
          escapeValue: false,
        },
      }}
    />
  ) : null;

  return (
    (hasMsg || hasTitle) && (
      <div ref={ref}>
        <Alert
          key={id}
          title={title}
          message={translation}
          scrollIntoView
          closable
          onClose={() => onClose(id)}
          className={className}
          {...alertProps}
        />
      </div>
    )
  );
};

/**
 * Component that renders all local alerts.
 */
export const LocalAlertsDisplay: React.FC<
  Pick<ComponentProps<'div'>, 'className' | 'style'>
> = ({ className, style }) => {
  const { alerts, removeAlert } = useLocalAlerts();

  return (
    alerts && (
      <div className={className} style={style}>
        {alerts.map((alert) => {
          return (
            <LocalAlert alert={alert} onClose={removeAlert} key={alert.id} />
          );
        })}
      </div>
    )
  );
};
